/**
 * 版本检测工具类
 * 用于检测域名可用性和版本一致性
 */
export class VersionUtils {
  /**
   * 检查域名可用性
   * @returns 返回检测结果
   * - 'unavailable': 域名不可用（请求失败）
   * - 'available': 域名可用（版本号一致）
   * - 'needs-refresh': 需要刷新（版本号不一致）
   */
  static async checkDomainAvailability(timeout: number = 8000, maxRetries: number = 2): Promise<'unavailable' | 'available' | 'needs-refresh'> {
    let lastError: unknown;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          console.log(`域名检测重试 (${attempt}/${maxRetries})...`);
        }

        // 1. 获取 /version.txt 文件（添加时间戳避免缓存）
        const timestamp = Date.now();
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const response = await fetch(`/version.txt?_t=${timestamp}`, {
          cache: 'no-store',
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          // 请求失败，记录错误并继续重试
          console.error('获取 version.txt 失败:', response.status, response.statusText);
          lastError = new Error(`HTTP ${response.status}: ${response.statusText}`);
          continue;
        }

        // 2. 读取远程版本号
        const remoteVersion = (await response.text()).trim();
        if (!remoteVersion) {
          console.warn('version.txt 内容为空');
          return 'unavailable';
        }

        // 3. 获取当前页面版本号（从 body 节点的 data-version 属性）
        const currentVersion = document.body.getAttribute('data-version');
        if (!currentVersion) {
          console.warn('当前页面没有 data-version 属性');
          // 如果当前页面没有版本号，但能获取到远程版本，认为需要刷新
          return 'needs-refresh';
        }

        console.log('版本检测 - 远程版本:', remoteVersion, '当前版本:', currentVersion);
        // 4. 比较版本号
        if (remoteVersion === currentVersion) {
          // 版本号一致，域名可用，走正常逻辑
          return 'available';
        } else {
          // 版本号不一致，有新版本，需要刷新
          console.info(`检测到新版本 (${currentVersion} → ${remoteVersion})，需要刷新页面`);
          return 'needs-refresh';
        }

      } catch (error) {
        // 请求出错（包括超时），记录错误并继续重试
        lastError = error;
        console.error(`域名检测失败 (尝试 ${attempt + 1}/${maxRetries + 1}):`, error);
      }
    }

    // 所有重试都失败，返回不可用
    console.error('域名检测最终失败，已重试', maxRetries, '次，最后错误:', lastError);
    return 'unavailable';
  }

  /**
   * 执行域名检测并处理结果
   * @param options 配置选项
   * @param options.onUnavailable 域名不可用时的回调
   * @param options.onNeedsRefresh 需要刷新时的回调
   * @param options.onAvailable 域名可用时的回调
   */
  static async checkAndHandle(options?: {
    onUnavailable?: () => void | Promise<void>;
    onNeedsRefresh?: () => void | Promise<void>;
    onAvailable?: () => void | Promise<void>;
  }): Promise<void> {
    const result = await this.checkDomainAvailability();

    switch (result) {
      case 'unavailable':
        // 域名不可用，跳转到访问受限页面
        console.error('域名不可用，跳转到错误页面');
        if (options?.onUnavailable) {
          await options.onUnavailable();
        } else {
          // 默认行为：跳转到错误页面
          await navigateTo('/error');
        }
        break;

      case 'needs-refresh':
        // 版本不一致，直接刷新当前页面
        console.info('检测到新版本，刷新页面');
        if (options?.onNeedsRefresh) {
          await options.onNeedsRefresh();
        } else {
          // 默认行为：刷新页面
          window.location.reload();
        }
        break;

      case 'available':
        // 域名可用，走正常逻辑
        console.log('域名检测通过，版本一致');
        if (options?.onAvailable) {
          await options.onAvailable();
        }
        break;
    }
  }

  /**
   * 带缓存的域名检测
   * @param cacheKey 缓存键名
   * @param cacheTime 缓存时间（毫秒），默认5分钟
   * @returns 返回检测结果
   */
  static async checkWithCache(
    cacheKey: string = 'domain_check_cache',
    cacheTime: number = 5 * 60 * 1000
  ): Promise<'unavailable' | 'available' | 'needs-refresh'> {
    // 检查缓存
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { result, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < cacheTime) {
          console.log('使用缓存的域名检测结果:', result);
          return result;
        }
      }
    }

    // 执行检测
    const result = await this.checkDomainAvailability();

    // 缓存结果（仅缓存成功的结果）
    if (typeof window !== 'undefined' && window.sessionStorage && result === 'available') {
      sessionStorage.setItem(cacheKey, JSON.stringify({
        result,
        timestamp: Date.now()
      }));
    }

    return result;
  }
}

/**
 * 便捷函数：执行域名检测
 */
export const checkDomain = () => VersionUtils.checkAndHandle();

/**
 * 便捷函数：仅获取域名状态
 */
export const getDomainStatus = () => VersionUtils.checkDomainAvailability();
